import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Menu as LucideMenu,
  X,
  User,
  Map,
  BarChart3,
  CreditCard,
  LogOut,
  Sparkles,
  ArrowRight,
  Compass,
  Trophy,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import AuthModal from "./auth/AuthModal";

const Logo = "/logo.png";

const SiteHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: 1200;
  padding: 14px 18px 0;
`;

const HeaderShell = styled.div`
  max-width: 1380px;
  margin: 0 auto;
  padding: 0 20px;
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: linear-gradient(
      180deg,
      rgba(10, 16, 28, 0.92),
      rgba(4, 8, 18, 0.92)
    ),
    rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(18px) saturate(140%);
  box-shadow: 0 24px 70px rgba(2, 6, 23, 0.34);

  @media (max-width: 920px) {
    border-radius: 22px;
    padding: 0 14px;
  }
`;

const HeaderRow = styled.div`
  min-height: 76px;
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
`;

const Brand = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  min-width: 0;
`;

const BrandMark = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: linear-gradient(
    135deg,
    rgba(245, 158, 11, 0.18),
    rgba(56, 189, 248, 0.16)
  );
  border: 1px solid rgba(148, 163, 184, 0.14);
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 30% 20%,
      rgba(255, 255, 255, 0.22),
      transparent 50%
    );
    pointer-events: none;
  }
`;

const LogoImage = styled.img`
  height: 45px;
  width: 45px;
  object-fit: contain;
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  @media (max-width: 560px) {
    display: none;
  }
`;

const BrandTitle = styled.div`
  color: #f8fafc;
  font-size: 15px;
  font-weight: 900;
  letter-spacing: 0.01em;
`;

const BrandSub = styled.div`
  margin-top: 2px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

const Nav = styled.nav`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px;
  width: min(660px, calc(100% - 520px));
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.56);
  backdrop-filter: blur(12px);

  @media (max-width: 980px) {
    display: none;
  }
`;

const NavItem = styled(NavLink)`
  position: relative;
  padding: 10px 15px;
  border-radius: 999px;
  text-decoration: none;
  color: ${({ theme, $featured }) =>
    $featured ? "#f8fafc" : theme.colors.textSecondary};
  font-size: 14px;
  font-weight: 700;
  transition: all 0.22s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  flex: 1 1 auto;
  min-width: 0;
  background: ${({ $featured }) =>
    $featured
      ? "linear-gradient(135deg, rgba(16, 185, 129, 0.18), rgba(56, 189, 248, 0.2))"
      : "transparent"};
  border: 1px solid
    ${({ $featured }) =>
      $featured ? "rgba(45, 212, 191, 0.22)" : "transparent"};
  box-shadow: ${({ $featured }) =>
    $featured ? "0 14px 28px rgba(8, 47, 73, 0.24)" : "none"};

  &:hover {
    color: #f8fafc;
    background: ${({ $featured }) =>
      $featured
        ? "linear-gradient(135deg, rgba(16, 185, 129, 0.22), rgba(56, 189, 248, 0.24))"
        : "rgba(255, 255, 255, 0.05)"};
  }

  &.active {
    color: #f8fafc;
    background: linear-gradient(
      135deg,
      rgba(245, 158, 11, 0.16),
      rgba(56, 189, 248, 0.14)
    );
    box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.12);
  }
`;

const FeaturedNavIcon = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.42);
  color: #5eead4;
`;

const Right = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CTAButton = styled(motion.button)`
  min-height: 42px;
  padding: 10px 14px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #f59e0b, #facc15);
  color: #04111f;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;
  box-shadow: 0 16px 34px rgba(245, 158, 11, 0.24);
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const SecondaryButton = styled.button`
  min-height: 42px;
  padding: 10px 13px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(15, 23, 42, 0.44);
  color: #e2e8f0;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.22s ease;

  &:hover {
    border-color: rgba(56, 189, 248, 0.34);
    color: #f8fafc;
    transform: translateY(-1px);
  }

  @media (max-width: 620px) {
    display: none;
  }
`;

const UserWrapper = styled.div`
  position: relative;
`;

const UserBtn = styled.button`
  height: 42px;
  min-width: 42px;
  padding: 0 12px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(15, 23, 42, 0.48);
  color: #f8fafc;
  cursor: pointer;
  transition: all 0.22s ease;

  &:hover {
    border-color: rgba(56, 189, 248, 0.34);
    transform: translateY(-1px);
  }
`;

const UserInitial = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  background: linear-gradient(
    135deg,
    rgba(245, 158, 11, 0.18),
    rgba(56, 189, 248, 0.16)
  );
  color: #f8fafc;
  font-size: 13px;
  font-weight: 900;
`;

const UserLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 138px;

  span:first-child {
    color: #f8fafc;
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span:last-child {
    color: #94a3b8;
    font-size: 10px;
    font-weight: 700;
  }

  @media (max-width: 1160px) {
    display: none;
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  right: 0;
  top: 56px;
  min-width: 260px;
  padding: 10px;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: linear-gradient(
      180deg,
      rgba(10, 16, 28, 0.98),
      rgba(4, 8, 18, 0.98)
    ),
    rgba(2, 6, 23, 0.92);
  box-shadow: 0 24px 80px rgba(2, 6, 23, 0.6);
  z-index: 2000;
`;

const DropdownHero = styled.div`
  padding: 14px 14px 12px;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(245, 158, 11, 0.12),
    rgba(56, 189, 248, 0.1)
  );
  border: 1px solid rgba(148, 163, 184, 0.12);
  margin-bottom: 8px;
`;

const DropdownName = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
`;

const DropdownEmail = styled.div`
  margin-top: 4px;
  color: #94a3b8;
  font-size: 12px;
  word-break: break-all;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 14px;
  border-radius: 16px;
  background: transparent;
  border: none;
  text-align: left;
  font-weight: 700;
  cursor: pointer;
  color: #e2e8f0;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.18s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #f8fafc;
  }
`;

const Divider = styled.div`
  height: 1px;
  margin: 8px 4px;
  background: rgba(148, 163, 184, 0.12);
`;

const LogoutItem = styled(DropdownItem)`
  color: #fca5a5;
`;

const MobileMenuBtn = styled.button`
  display: none;
  height: 42px;
  width: 42px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(15, 23, 42, 0.48);
  color: #f8fafc;
  place-items: center;
  cursor: pointer;

  @media (max-width: 980px) {
    display: grid;
  }
`;

const MobilePanel = styled(motion.div)`
  position: absolute;
  right: 0;
  top: calc(100% + 10px);
  width: min(100%, 380px);
  padding: 14px;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: linear-gradient(
      180deg,
      rgba(10, 16, 28, 0.98),
      rgba(4, 8, 18, 0.98)
    ),
    rgba(2, 6, 23, 0.92);
  box-shadow: 0 24px 80px rgba(2, 6, 23, 0.6);

  @media (min-width: 981px) {
    display: none;
  }
`;

const MobileBlock = styled.div`
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(15, 23, 42, 0.48);
`;

const MobileTitle = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
`;

const MobileSub = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.55;
`;

const MobileNav = styled.div`
  margin-top: 12px;
  display: grid;
  gap: 8px;
`;

const MobileNavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 14px;
  border-radius: 16px;
  text-decoration: none;
  color: #e2e8f0;
  font-size: 14px;
  font-weight: 800;
  background: ${({ $featured }) =>
    $featured
      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.16), rgba(56, 189, 248, 0.14))"
      : "rgba(15, 23, 42, 0.42)"};
  border: 1px solid
    ${({ $featured }) =>
      $featured ? "rgba(245, 158, 11, 0.24)" : "rgba(148, 163, 184, 0.1)"};
  box-shadow: ${({ $featured }) =>
    $featured ? "0 14px 28px rgba(2, 6, 23, 0.18)" : "none"};

  &.active {
    color: #f8fafc;
    background: linear-gradient(
      135deg,
      rgba(245, 158, 11, 0.14),
      rgba(56, 189, 248, 0.12)
    );
  }
`;

const MobileNavLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const MobileActions = styled.div`
  margin-top: 12px;
  display: grid;
  gap: 10px;
`;

const MobileSecondaryButton = styled(SecondaryButton)`
  display: inline-flex;
  justify-content: center;
  width: 100%;
`;

const MobilePrimaryButton = styled(CTAButton)`
  justify-content: center;
  width: 100%;
`;

const MENU = [
  { label: "Home", href: "/" },
  { label: "Roadmaps", href: "/roadmaps" },
  { label: "Learners", href: "/learners" },
  { label: "Community", href: "/community/feeds" },
  { label: "AI Mentors", href: "/ai-mentors" },
  { label: "Pricing", href: "/pricing" },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const dropdownRef = useRef(null);

  const openAuthModal = (mode) => {
    setAuthMode(mode);
    setAuthOpen(true);
    setMobileOpen(false);
  };

  const handleAuthSuccess = (mode) => {
    setAuthOpen(false);
    navigate("/");
  };

  useEffect(() => {
    setMobileOpen(false);
    setUserOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setUserOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <SiteHeader>
      <HeaderShell>
        <HeaderRow>
          <Brand to="/">
            <BrandMark>
              <LogoImage src={Logo} alt="skilll.dev logo" />
            </BrandMark>
            <BrandText>
              <BrandTitle>skilll.dev</BrandTitle>
              <BrandSub>Roadmap clarity for serious learners</BrandSub>
            </BrandText>
          </Brand>

          <Nav>
            {MENU.map((item) => (
              <NavItem
                key={item.label}
                to={item.href}
                end={item.href === "/"}
                $featured={item.label === "AI Mentors"}
              >
                {item.label === "AI Mentors" && (
                  <FeaturedNavIcon>
                    <Sparkles size={13} />
                  </FeaturedNavIcon>
                )}
                {item.label}
              </NavItem>
            ))}
          </Nav>

          <Right>
            {user ? (
              <UserWrapper ref={dropdownRef}>
                <UserBtn onClick={() => setUserOpen((v) => !v)}>
                  <UserInitial>
                    {user?.name?.charAt(0)?.toUpperCase() || <User size={14} />}
                  </UserInitial>
                  <UserLabel>
                    <span>{user?.name || "Your account"}</span>
                  </UserLabel>
                </UserBtn>

                <AnimatePresence>
                  {userOpen && (
                    <UserDropdown
                      as={motion.div}
                      initial={{ opacity: 0, y: 10, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.98 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                    >
                      <DropdownHero>
                        <DropdownName>
                          {user?.name || "Your account"}
                        </DropdownName>
                        <DropdownEmail>{user?.email}</DropdownEmail>
                      </DropdownHero>

                      <DropdownItem onClick={() => navigate("/profile")}>
                        <User size={16} /> My Profile
                      </DropdownItem>
                      <DropdownItem onClick={() => navigate("/my-roadmap")}>
                        <Map size={16} /> My Roadmap
                      </DropdownItem>
                      <DropdownItem onClick={() => navigate("/dashboard")}>
                        <BarChart3 size={16} /> Progress Dashboard
                      </DropdownItem>
                      <DropdownItem onClick={() => navigate("/learners")}>
                        <Trophy size={16} /> Learners
                      </DropdownItem>
                      <DropdownItem onClick={() => navigate("/my-subscriptions")}>
                        <CreditCard size={16} /> My Subscriptions
                      </DropdownItem>
                      <DropdownItem onClick={() => navigate("/pricing")}>
                        <CreditCard size={16} /> Plans & Pricing
                      </DropdownItem>

                      <Divider />

                      <LogoutItem
                        onClick={async () => {
                          await logout();
                          navigate("/");
                        }}
                      >
                        <LogOut size={16} /> Logout
                      </LogoutItem>
                    </UserDropdown>
                  )}
                </AnimatePresence>
              </UserWrapper>
            ) : (
              <>
                <SecondaryButton onClick={() => openAuthModal("login")}>
                  Login
                </SecondaryButton>
                <CTAButton
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => openAuthModal("signup")}
                >
                  Create
                  <ArrowRight size={15} />
                </CTAButton>
              </>
            )}

            <MobileMenuBtn onClick={() => setMobileOpen((s) => !s)}>
              {mobileOpen ? <X size={18} /> : <LucideMenu size={18} />}
            </MobileMenuBtn>
          </Right>

          <AnimatePresence>
            {mobileOpen && (
              <MobilePanel
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.98 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <MobileBlock>
                  <MobileTitle>
                    {user
                      ? "Continue your roadmap"
                      : "Career clarity starts here"}
                  </MobileTitle>
                  <MobileSub>
                    {user
                      ? "Jump back into your roadmap, dashboard, and progress system."
                      : "Get a roadmap that tells you what to learn, what to build, and what to do next."}
                  </MobileSub>
                </MobileBlock>

                <MobileNav>
                  {MENU.map((item) => (
                    <MobileNavItem
                      key={item.label}
                      to={item.href}
                      end={item.href === "/"}
                      $featured={item.label === "AI Mentors"}
                    >
                      <MobileNavLabel>
                        {item.label === "AI Mentors" && <Sparkles size={15} />}
                        {item.label}
                      </MobileNavLabel>
                      <Compass size={16} />
                    </MobileNavItem>
                  ))}
                </MobileNav>

                {!user && (
                  <MobileActions>
                    <MobileSecondaryButton
                      onClick={() => openAuthModal("login")}
                    >
                      Login
                    </MobileSecondaryButton>
                    <MobilePrimaryButton
                      whileTap={{ scale: 0.97 }}
                      onClick={() => openAuthModal("signup")}
                    >
                      Create account
                      <ArrowRight size={16} />
                    </MobilePrimaryButton>
                  </MobileActions>
                )}
              </MobilePanel>
            )}
          </AnimatePresence>

          <AuthModal
            open={authOpen}
            initialMode={authMode}
            onClose={() => setAuthOpen(false)}
            onSuccess={handleAuthSuccess}
          />
        </HeaderRow>
      </HeaderShell>
    </SiteHeader>
  );
}
